import React, { useState, useMemo, useEffect, useContext } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
    LayoutDashboard,
    Plug,
    FileText,
    Menu as MenuIcon,
    ChevronLeft,
    ChevronRight,
    X,
} from 'lucide-react'
import styles from './ComConfigShell.module.css'
import ActiveStoreContext from '../../../../store/ActiveStoreContext'

const NAV_GROUPS = [
    {
        label: 'Overview',
        items: [
            { to: '/communication-settings', label: 'Events', icon: LayoutDashboard, end: true },
        ],
    },
    {
        label: 'Channels',
        items: [
            { to: '/communication-settings/sender-connections', label: 'Sender connections', icon: Plug },
        ],
    },
    {
        label: 'Templates',
        items: [
            { to: '/communication-settings/my-templates', label: 'My templates', icon: FileText },
        ],
    },
]

const ComConfigShell = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return asCtx?.activeStore && asCtx.activeStore.trim() !== ''
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('comconfig_sidebar_collapsed') === '1' } catch { return false }
    })
    const [mobileNavOpen, setMobileNavOpen] = useState(false)

    useEffect(() => {
        try { localStorage.setItem('comconfig_sidebar_collapsed', collapsed ? '1' : '0') } catch {}
    }, [collapsed])

    useEffect(() => {
        setMobileNavOpen(false)
    }, [location.pathname])

    const storeName = activeStore?.storeName || activeStore?.storeIdentifier || '—'

    const renderNav = (forMobile = false) => (
        <nav className={styles.nav}>
            {NAV_GROUPS.map((group) => (
                <div key={group.label} className={styles.navGroup}>
                    {(!collapsed || forMobile) && (
                        <div className={styles.navGroupLabel}>{group.label}</div>
                    )}
                    <ul className={styles.navList}>
                        {group.items.map((item) => {
                            const Icon = item.icon
                            return (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        className={({ isActive }) =>
                                            `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                        }
                                        title={collapsed && !forMobile ? item.label : undefined}
                                    >
                                        <Icon size={16} className={styles.navItemIcon} strokeWidth={1.75} />
                                        {(!collapsed || forMobile) && (
                                            <span className={styles.navItemLabel}>{item.label}</span>
                                        )}
                                    </NavLink>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ))}
        </nav>
    )

    return (
        <div className={styles.shell}>
            {/* ── Sidebar (desktop) ───────────────────────────────── */}
            <aside
                className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}
                aria-label="Communication settings navigation"
            >
                <div className={styles.sidebarHeader}>
                    {!collapsed && <span className={styles.sidebarHeaderLabel}>Communications</span>}
                    <button
                        type="button"
                        className={styles.collapseBtn}
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                    </button>
                </div>
                {renderNav()}
            </aside>

            {/* ── Mobile nav drawer ───────────────────────────────── */}
            {mobileNavOpen && (
                <>
                    <button
                        type="button"
                        className={styles.mobileOverlay}
                        onClick={() => setMobileNavOpen(false)}
                        aria-label="Close navigation"
                    />
                    <aside className={styles.mobileDrawer} aria-label="Communication settings navigation">
                        <div className={styles.mobileDrawerHeader}>
                            <span className={styles.sidebarHeaderLabel}>Communications</span>
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={() => setMobileNavOpen(false)}
                                aria-label="Close navigation"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {renderNav(true)}
                    </aside>
                </>
            )}

            {/* ── Main column ─────────────────────────────────────── */}
            <div className={styles.main}>
                <header className={styles.topBar}>
                    <div className={styles.topBarLeft}>
                        <button
                            type="button"
                            className={styles.mobileMenuBtn}
                            onClick={() => setMobileNavOpen(true)}
                            aria-label="Open navigation"
                        >
                            <MenuIcon size={18} />
                        </button>
                        <span className={styles.storeName}>{storeName}</span>
                        <span className={styles.sectionBadge}>Communication settings</span>
                    </div>
                </header>

                <main className={styles.pageOutlet}>
                    <Outlet />
                </main>
            </div>
        </div>
    )
}

export default ComConfigShell
